import { FC, useEffect, useMemo } from 'react'
import { useActiveFilterDispatch } from '@/providers/filterProvider/filtersProvider'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { Box } from '@/components/atoms/Grid'
import ProductListingTitleCountInfo from '@/components/molecules/productListingTitleCountInfo'
import ManufacturerProductsContainer from '@/components/organisms/manufacturerProductsContainer'
import {
  ProductListingFetcher,
  ProductListingFetcherResponse
} from '@/components/organisms/productListing/productListingFetcher'
import { Config } from '@/core/config'
import {
  HomeBannerSize,
  ListingProducts,
  Manufacturer,
  Sdk
} from '@/network/graphql.g'
import {
  withFilterProvider,
  WithFilterProviderProps
} from '@/HOC/withFilterProvider'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { isInteger } from '@/utils/typeHelper'
import { useUser } from '@/providers/userProvider'
import { useMhub } from '@/providers/mhubProvider'
import { getCachableHeader } from '@/core/utils'
import useProductListing from 'hooks/useProductListing'
import { convertAPIProductToProduct } from '@/types/bridgeProduct'
import { getProductListingData } from '@/components/organisms/productListing/productListingRedesign'
import { GetServerSidePropsContext } from 'next'
import { getMainData } from '@/utils/mainDataHelper'
import { SentryError } from '@/core/sentryError'
import { CacheType } from '@/utils/cache/pageDataCache'
import { useAdditionalInfoProvider } from '@/providers/additionalInfoProvider'
import { useRouter } from 'next/router'
import { useNavigationSource } from '@/providers/navigationSourceProvider'

type ManufacturerDetailProps = {
  manufacturerProducts: ListingProducts
  manufacturer: Manufacturer
  filterId: string
  requestId: string
  isPreload: boolean
} & WithFilterProviderProps

const ManufacturerDetail: FC<ManufacturerDetailProps> = ({
  manufacturerProducts,
  manufacturer,
  filterId,
  requestId,
  isPreload
}) => {
  const { isLoggedIn, isEmployee } = useUser()
  const { setProductIds } = useAdditionalInfoProvider()

  const { pushManufacturerDetailPage, pushSimpleManufacturerDetailPage } =
    useMhub()
  useEffect(() => {
    pushManufacturerDetailPage(manufacturer)
  }, [manufacturer])

  const router = useRouter()

  const { setFilters } = useActiveFilterDispatch()
  const { requestNotifyError } = useGraphQl()
  const fetcher: ProductListingFetcher = async (
    page,
    filterInput,
    refresh,
    initialLoad = false
  ): Promise<ProductListingFetcherResponse> => {
    const result = await requestNotifyError(
      (sdk: Sdk) => ({
        method: sdk.getNextProductsForManufacturer,
        variables: {
          limit: Config.perPage,
          id: manufacturer?.id,
          page: page,
          filter: filterInput,
          withFilterAndProductsCount: refresh
        },
        requestHeaders: getCachableHeader(filterInput, isLoggedIn)
      }),
      true
    )
    if (!result.success) {
      throw (
        result.error ??
        new SentryError('Manufacturer product listing', result.response)
      )
    }

    const { products, filter, totalPagesCount } =
      result.response?.manufacturerProducts ?? {}
    if (filter) {
      setFilters(filter)
    }

    !initialLoad && pushSimpleManufacturerDetailPage(manufacturer, page)

    if (isEmployee) {
      setProductIds((prev) => [...prev, ...products.map((p) => p?.id)])
    }

    if (!initialLoad && !router.query.a) {
      window.scrollTo(0, 0)
    }

    return {
      products:
        products?.map((product) => convertAPIProductToProduct(product)) ?? [],
      totalPagesCount
    }
  }

  const { data } = useProductListing({
    totalPagesCount: manufacturerProducts?.totalPagesCount,
    totalProductsCount: manufacturerProducts?.totalProductsCount,
    id: manufacturer?.id,
    products: manufacturerProducts?.products,
    banners: useMemo(
      () =>
        manufacturer?.brandPanel
          ? [
              {
                id: '',
                imageBig: manufacturer?.brandPanel,
                imageSmall: manufacturer?.brandPanelMobile,
                size: HomeBannerSize.Big
              }
            ]
          : null,
      [manufacturer?.brandPanel, manufacturer?.brandPanelMobile]
    ),
    filterId,
    requestId,
    isPreload,
    fetcher
  })

  const { setNavigationSource } = useNavigationSource()

  useEffect(() => {
    setNavigationSource({
      from: 'manufacturer',
      name: manufacturer?.name,
      slug: `/manufacturer/${manufacturer?.id}`
    })
  }, [])

  return (
    <PageContentWrapper>
      <Box key={manufacturer?.id}>
        <ProductListingTitleCountInfo title={manufacturer?.name} />
        <ManufacturerProductsContainer
          productListingFetcher={fetcher}
          data={data}
          description={manufacturer?.description}
          showFilteredBy={true}
          list={`list.brand.${manufacturer?.name}`}
        />
      </Box>
    </PageContentWrapper>
  )
}

export default withFilterProvider(ManufacturerDetail)

const getCommonProps = (
  context: GetServerSidePropsContext,
  props?: { requestId: string }
): { requestId: string } => ({
  requestId: props?.requestId ?? null
})

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore,
    session
  }): Promise<GetServerSideProps<ManufacturerDetailProps>> => {
    const productListingData = getProductListingData(context)
    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }
    const { page, limit, filterInput, filterId, requestId, isPreload } =
      productListingData

    const manufacturerId = context.query.id as string
    if (!isInteger(manufacturerId)) {
      return {
        notFound: true
      }
    }
    const { success, response } = await requestThrow401({
      method: sdk.getProductsForManufacturer,
      variables: {
        limit,
        id: manufacturerId,
        filter: filterInput,
        page,
        withMainNavigation,
        withStore
      },
      requestHeaders: getCachableHeader(filterInput, session?.isLoggedIn),
      successPredicate: (response) =>
        !!(response?.manufacturerProducts && response?.manufacturer)
    })

    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        ...{
          mainData: getMainData(response),
          seoTags: response?.seoTags,
          filter: response?.manufacturerProducts.filter,
          manufacturerProducts: response?.manufacturerProducts,
          manufacturer: response?.manufacturer,
          filterId,
          isPreload,
          page,
          disableDaktela: true
        },
        ...getCommonProps(context, { requestId })
      }
    }
  },
  { cacheType: CacheType.ManufacturerDetail, getCommonProps }
)
